/**
 * CostBasisForm Component
 *
 * Form for setting or updating manual cost-basis overrides
 * Used within transaction detail modals
 */

import React, { useState } from "react";
import { InputNumber } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useFormTracking } from "@/hooks/useFormTracking";

export interface CostBasisFormProps {
  txnId: number;
  initialValue?: number;
  initialNote?: string;
  onSubmit: (costBasis: number, note?: string) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export const CostBasisForm: React.FC<CostBasisFormProps> = ({
  txnId,
  initialValue,
  initialNote,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [costBasis, setCostBasis] = useState<number | null>(initialValue ?? null);
  const [note, setNote] = useState<string>(initialNote ?? "");
  const [error, setError] = useState<string | null>(null);

  const formTracking = useFormTracking({ formName: "cost_basis_form" });

  const handleSubmit = () => {
    // Validation
    if (costBasis === null || costBasis === undefined) {
      setError("Cost basis is required");
      formTracking.trackValidationFailed("cost_basis", "required", "Cost basis is required", "number");
      return;
    }

    if (costBasis < 0) {
      setError("Cost basis must be 0 or greater");
      formTracking.trackValidationFailed("cost_basis", "invalid_value", "Cost basis must be 0 or greater", "number");
      return;
    }

    // Clear any previous errors
    setError(null);

    // Track form submit
    formTracking.trackFormSubmit({ txn_id: txnId, has_note: !!note });

    // Submit
    onSubmit(costBasis, note || undefined);
  };

  const hasChanges = () => {
    return (
      costBasis !== (initialValue ?? null) ||
      note !== (initialNote ?? "")
    );
  };

  return (
    <div className="space-y-4">
      {/* Cost Basis Input */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Manual Cost Basis
        </label>
        <InputNumber
          placeholder="Enter cost basis value"
          value={costBasis}
          onFocus={() => formTracking.trackFieldFocus("cost_basis", "number")}
          onChange={(val) => {
            setCostBasis(val);
            setError(null);
            formTracking.trackFieldChange("cost_basis", "number");
          }}
          min={0}
          step={0.01}
          precision={2}
          disabled={isLoading}
        />
        {error && (
          <p className="text-sm text-ms-neg mt-1">
            {error}
          </p>
        )}
        <p className="text-xs text-ms-mute mt-1">
          This will override the automatically calculated cost basis
        </p>
      </div>

      {/* Note Input */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Note (Optional)
        </label>
        <textarea
          rows={3}
          className="flex w-full border border-ms-line bg-transparent px-3 py-2 text-sm text-ms-ink transition-colors placeholder:text-ms-dim focus:border-ms-ink focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          placeholder="Add a note explaining why you set this manual cost basis (e.g., 'Based on broker statement')"
          maxLength={500}
          value={note}
          onFocus={() => formTracking.trackFieldFocus("note", "textarea")}
          onChange={(e) => {
            setNote(e.target.value);
            formTracking.trackFieldChange("note", "textarea");
          }}
          disabled={isLoading}
        />
        <p className="text-xs text-ms-dim mt-1 font-mono tabular-nums">
          {note.length}/500 characters
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-2 pt-2">
        <Button
          variant="outline"
          onClick={onCancel}
          disabled={isLoading}
        >
          <XMarkIcon className="h-4 w-4 mr-1" />
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!hasChanges() || isLoading}
        >
          <CheckIcon className="h-4 w-4 mr-1" />
          {isLoading ? "Saving..." : "Save Cost Basis"}
        </Button>
      </div>
    </div>
  );
};
